import {
  EditorView,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
} from '@codemirror/view';
import { EditorState, type Extension } from '@codemirror/state';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands';
import { html } from '@codemirror/lang-html';
import {
  bracketMatching,
  syntaxHighlighting,
  HighlightStyle,
} from '@codemirror/language';
import { closeBrackets } from '@codemirror/autocomplete';
import { lintGutter } from '@codemirror/lint';
import { tags } from '@lezer/highlight';
import { htmlLinter } from './htmlLint';

/**
 * Editor chrome themed from index.css CSS vars. CSS vars (incl. light-dark())
 * resolve natively in CodeMirror's injected stylesheet, so light/dark flips for
 * free — no JS color probe needed here (that's only for JS readers; see
 * chrome/resolveCssColor.ts). Cool palette + tokens per styleguide §05.
 */
export const cmTheme = EditorView.theme({
  '&': {
    background: 'var(--color-surface-2)',
    color: 'var(--color-text)',
    height: '100%',
    fontSize: '15px',
  },
  '.cm-scroller': {
    fontFamily: 'var(--font-mono)',
    lineHeight: '1.7',
  },
  '.cm-content': { caretColor: 'var(--color-accent)' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--color-accent)' },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
    { background: 'color-mix(in oklab, var(--color-accent) 22%, transparent)' },
  '.cm-gutters': {
    background: 'var(--color-surface-2)',
    color: 'var(--color-text-muted)',
    borderRight: '1px solid var(--color-border)',
  },
  '.cm-activeLine': { background: 'color-mix(in oklab, var(--color-accent) 7%, transparent)' },
  '.cm-activeLineGutter': { background: 'color-mix(in oklab, var(--color-accent) 12%, transparent)' },
  '.cm-lineNumbers .cm-gutterElement': { color: 'var(--color-faint)' },
});

/** Lezer highlight tags → cool palette (styleguide §05: tag=accent, attr/str=accent-2). */
export const cmHighlight = HighlightStyle.define([
  { tag: tags.tagName, color: 'var(--color-accent)' },
  { tag: tags.angleBracket, color: 'var(--color-text-muted)' },
  { tag: tags.attributeName, color: 'var(--color-accent-2)' },
  { tag: tags.attributeValue, color: 'var(--color-accent-2)' },
  { tag: tags.string, color: 'var(--color-accent-2)' },
  { tag: tags.comment, color: 'var(--color-text-muted)', fontStyle: 'italic' },
  { tag: tags.content, color: 'var(--color-text)' },
]);

export interface BuildExtensionsOpts {
  readOnly: boolean;
  onChange?: (next: string) => void;
  onFormat?: () => void;
}

export function buildExtensions({
  readOnly,
  onChange,
  onFormat,
}: BuildExtensionsOpts): Extension[] {
  const formatKeybinding = {
    key: 'Shift-Alt-f',
    run: () => {
      onFormat?.();
      return true;
    },
  };

  const base: Extension[] = [
    lineNumbers(),
    html(),
    bracketMatching(),
    htmlLinter,
    lintGutter(),
    cmTheme,
    syntaxHighlighting(cmHighlight),
    EditorView.lineWrapping,
  ];

  if (readOnly) {
    base.push(
      EditorState.readOnly.of(true),
      EditorView.editable.of(false),
      keymap.of([formatKeybinding]),
    );
    return base;
  }

  base.push(
    highlightActiveLine(),
    highlightActiveLineGutter(),
    history(),
    closeBrackets(),
    keymap.of([
      ...defaultKeymap,
      ...historyKeymap,
      indentWithTab,
      formatKeybinding,
    ]),
  );

  if (onChange) {
    base.push(
      EditorView.updateListener.of((update) => {
        if (update.docChanged) onChange(update.state.doc.toString());
      }),
    );
  }

  return base;
}
